import { modelOptions, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { E164PhoneNumber, getZodSchemaFromClass, validate } from "../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type { ChannelAddressFieldsWithDefaults, ChannelAddressSchemaTypes } from "../types/channelAddress.d.js";
import type { DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "ChannelAddress" as const;

/** WeChat is planned but unintegrated, and is the only channel whose address is not a phone number. */
export const MESSAGING_CHANNELS = ["sms", "rcs", "whatsapp"] as const;

export type MessagingChannel = (typeof MESSAGING_CHANNELS)[number];

/**
 * Joins a channel to one of its values as a key that survives our transports: BullMQ rejects a colon in a custom
 * job id, and Redis reads braces as a cluster hash tag. Percent-encoding is injective, so no two values collapse
 * onto one key — which matters because these keys are how duplicates are suppressed.
 */
export function channelScopedKey(channel: MessagingChannel, value: string): string {
    return `${channel}-${encodeURIComponent(value)}`;
}

/**
 * One endpoint we can exchange messages with; an address is only routable with a channel, so they travel together.
 * A value, not a role — the field naming it says which end it is: `sender` inbound, `recipient` outbound.
 */
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<ChannelAddress>>(...addSchemaKeyHook(SCHEMA_KEY))
export class ChannelAddress extends SchemaBase<ChannelAddress, ChannelAddressFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: z.enum(MESSAGING_CHANNELS) })
    @prop({ type: () => String, required: true, enum: MESSAGING_CHANNELS })
    public channel!: MessagingChannel;

    /**
     * The address with any channel prefix stripped. Every channel we accept is phone-backed, so E.164 is the whole
     * rule today; another format discriminates this on `channel`, which both fields being in scope here allows.
     * An addressing key, not an identity — handsets change hands.
     */
    @validate({ zodValidator: E164PhoneNumber })
    @prop({ type: () => String, required: true })
    public address!: string;

    static getters = {
        /** The endpoint as one key — see {@link channelScopedKey}. */
        getKey: (self: Pick<ChannelAddress, "channel" | "address">): string =>
            channelScopedKey(self.channel, self.address),
    } as const satisfies Record<string, (self: ChannelAddress) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<ChannelAddressSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<ChannelAddress, ChannelAddressFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

ChannelAddress.dtoValidators = getZodSchemaFromClass(
    ChannelAddress,
) as unknown as DtoValidatorTypes<ChannelAddressSchemaTypes>;
